import { PermActions, type ESubjects, type TPermissionAction } from "@/api/models/auth/ability";
import type { NestedKeyOf, OneOrManyOrNull } from "@/types/general";
import { computed, toValue, type MaybeRefOrGetter } from "vue";
import { usePendingTermsStore } from "@/stores/pendingTermsStore";

type TCanFn = (action: string, subject: string) => boolean;

export type TUseQueryEnabledOptions<TFilters = any> = {
  /** Base enabled flag */
  enabled?: MaybeRefOrGetter<boolean>;
  /** Optional CASL permission subjects to check for READ access */
  permission?: MaybeRefOrGetter<OneOrManyOrNull<ESubjects> | undefined | null>;
  /** Property paths within `filters` that must be defined to enable the query */
  neededKey?: MaybeRefOrGetter<NestedKeyOf<TFilters>[] | undefined>;
  /** The filters object to check `neededKey` against */
  filters?: MaybeRefOrGetter<TFilters | undefined>;

  action?: MaybeRefOrGetter<TPermissionAction>;
  can: TCanFn;
  /**
   * Keep the query enabled while pending terms block the app. Only for the terms documents
   * themselves — the user has to be able to read what they're being asked to accept.
   */
  ignorePendingTermsBlock?: boolean;
};

/**
 * Shared helper to compute the `enabled` state for Vue Query wrappers like `useGenericListQuery` and `useGenericPaginatedQuery`.
 * Centralizes the logic for permission checks and required filter keys.
 */
export const useQueryEnabled = <TFilters = any>(options: TUseQueryEnabledOptions<TFilters>) => {
  const pendingTermsStore = usePendingTermsStore();
  return computed(() => {
    if (pendingTermsStore.isBlocked && !options.ignorePendingTermsBlock) return false;
    const isEnabled = toValue(options.enabled) ?? true;
    const permission = toValue(options.permission);
    const neededKey = toValue(options.neededKey) as string[] | undefined;
    const action = toValue(options.action) ?? PermActions.READ;
    const filters = toValue(options.filters);

    return (
      isEnabled &&
      (neededKey && neededKey.length
        ? neededKey.every((key) => {
            const value = key.includes(".")
              ? key.split(".").reduce((acc: any, part) => acc?.[part], filters)
              : (filters as any)?.[key];
            return value !== undefined;
          })
        : true) &&
      (permission ? options.can(action, permission as string) : true)
    );
  });
};
